import React, { useState, useEffect } from 'react';
import { Box, Popover, Typography } from '@mui/material';
import { HexColorPicker, HexColorInput } from 'react-colorful';

interface CompactColorPickerProps {
  label: string;
  color: string;
  onChange: (color: string) => void;
}

const CompactColorPicker: React.FC<CompactColorPickerProps> = ({
  label,
  color,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [internalColor, setInternalColor] = useState(color);

  // Update internal color when the prop changes (e.g., from parent state)
  useEffect(() => {
    setInternalColor(color);
  }, [color]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    // Ensure the final selected color is propagated when the popover closes
    if (internalColor !== color) {
      onChange(internalColor);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  // Debounced onChange handler for live updates while the popover is open
  useEffect(() => {
    if (!open) {
      return; // Only run debounce logic when the popover is open
    }

    const handler = setTimeout(() => {
      // After 300ms of no changes, if the color is different, propagate it.
      if (internalColor !== color) {
        onChange(internalColor);
      }
    }, 300); // 300ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [internalColor, onChange, color, open]);

  const handleChange = (newColor: string) => {
    setInternalColor(newColor);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        my: 1,
      }}
    >
      <Typography variant="body2">{label}</Typography>
      <Box
        aria-describedby={id}
        component="button"
        onClick={handleClick}
        sx={{
          width: 28,
          height: 28,
          borderRadius: '4px',
          border: '1px solid #ccc',
          backgroundColor: internalColor,
          cursor: 'pointer',
          p: 0, // remove padding from button element
          '&:hover': {
            opacity: 0.9,
          },
        }}
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <HexColorPicker color={internalColor} onChange={handleChange} />
          <HexColorInput
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              textAlign: 'center',
              textTransform: 'uppercase',
            }}
            color={internalColor}
            onChange={handleChange}
            prefixed
          />
        </Box>
      </Popover>
    </Box>
  );
};

export default CompactColorPicker;
